import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuthStore } from '../store/authStore.js';
import { useMessageStore } from '../store/messageStore.js';
import { useContactStore } from '../store/contactStore.js';

let socket = null;

export function useSocket() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const addMessage = useMessageStore((s) => s.addMessage);
  const updateMessageStatus = useMessageStore((s) => s.updateMessageStatus);
  const updateContact = useContactStore((s) => s.updateContact);
  const incrementUnread = useContactStore((s) => s.incrementUnread);
  const touchContactFromMessage = useContactStore((s) => s.touchContactFromMessage);
  const setUnreadCount = useContactStore((s) => s.setUnreadCount);
  const clearAllUnread = useContactStore((s) => s.clearAllUnread);

  // Use a ref so the event handler always sees fresh selectedContact
  const selectedContactRef = useRef(useContactStore.getState().selectedContact);
  useEffect(() => {
    return useContactStore.subscribe((state) => {
      selectedContactRef.current = state.selectedContact;
    });
  }, []);

  useEffect(() => {
    if (!accessToken) return;

    socket = io('/', {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
    });

    socket.on('new_message', (message) => {
      addMessage(message.contactId, message);

      // Always update the contact's lastMessageAt + preview + resort
      touchContactFromMessage(message);

      // Increment unread only for inbound messages on non-selected contacts
      if (
        message.direction === 'INBOUND' &&
        selectedContactRef.current?.id !== message.contactId
      ) {
        incrementUnread(message.contactId);
      }
    });

    socket.on('message_status_update', ({ twilioSid, status, errorMessage }) => {
      const messages = useMessageStore.getState().messages;
      for (const [contactId, msgs] of Object.entries(messages)) {
        if (msgs.find((m) => m.twilioSid === twilioSid)) {
          updateMessageStatus(contactId, twilioSid, status, errorMessage);
          break;
        }
      }
    });

    socket.on('contact_updated', (contact) => {
      updateContact(contact);
    });

    socket.on('contact_unread_updated', ({ contactId, unreadCount }) => {
      setUnreadCount(contactId, unreadCount);
    });

    socket.on('all_unread_cleared', () => {
      clearAllUnread();
    });

    return () => {
      socket?.disconnect();
      socket = null;
    };
  }, [accessToken]);

  return socket;
}

export function joinContactRoom(contactId) {
  socket?.emit('join_contact', { contactId });
}

export function leaveContactRoom(contactId) {
  socket?.emit('leave_contact', { contactId });
}

export function getSocket() {
  return socket;
}
